// src/services/matterTemplateService.ts
import type { MatterNumberingTemplate } from '../types/matterNumbering';

export interface MatterTemplate {
  id: string;
  name: string;
  description: string;
  practiceArea?: string;
  numberingTemplate?: MatterNumberingTemplate;
  isCustom?: boolean;
  createdBy?: string;
  createdAt?: string;
}

export class MatterTemplateService {
  /**
   * Get authentication headers with Keycloak token
   * Note: This method requires token to be passed from React component with Keycloak context
   */
  private static getAuthHeaders(token: string) {
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Get all available matter templates for the current firm
   * @param token - Keycloak JWT token from React context
   */
  static async getMatterTemplates(token: string): Promise<MatterTemplate[]> {
    try {
      // Get matter numbering configuration
      const response = await fetch('/api/v1/admin/firm-settings/matter-configuration', {
        method: 'GET',
        headers: this.getAuthHeaders(token),
      });

      if (response.ok) {
        const data = await response.json();
        const config = data.config;
        
        if (!config) return [];

        const templates: MatterTemplate[] = [];

        // Add default template
        templates.push({
          id: 'default',
          name: 'Default Template',
          description: 'Standard matter setup',
          numberingTemplate: config.defaultTemplate
        });

        // Add custom templates from Matter Builder
        if (config.customTemplates && config.customTemplates.length > 0) {
          const enabledTemplates = config.customTemplates.filter((template: MatterNumberingTemplate) => template.status === 'enabled');
          
          enabledTemplates.forEach((template: MatterNumberingTemplate) => {
              templates.push({
                id: template.id,
                name: template.name,
                description: template.description,
                practiceArea: Array.isArray(template.practiceAreas) 
                  ? template.practiceAreas[0] 
                  : undefined,
                numberingTemplate: template,
                isCustom: true,
                createdBy: template.createdBy,
                createdAt: template.createdAt
              });
            });
        }

        // Add practice area specific templates — only if they have a real
        // numbering pattern configured (not just an "enabled" stub)
        if (config.practiceAreaConfigs) {
          config.practiceAreaConfigs.forEach((paConfig: any) => {
            if (
              paConfig.isEnabled &&
              paConfig.template &&
              paConfig.template.pattern &&
              paConfig.practiceAreaName
            ) {
              templates.push({
                id: `pa-${paConfig.practiceArea}`,
                name: `${paConfig.practiceAreaName} Template`,
                description: `Practice area specific template for ${paConfig.practiceAreaName}`,
                practiceArea: paConfig.practiceArea,
                numberingTemplate: paConfig.template,
                isCustom: false
              });
            }
          });
        }

        return templates;
      }

      console.error('Failed to fetch matter configuration:', response.status, response.statusText);
      return [];
    } catch (error) {
      console.error('Error fetching matter templates:', error);
      return [];
    }
  }

  /**
   * Get templates filtered by practice area
   * @param token - Keycloak JWT token from React context
   */
  static async getTemplatesByPracticeArea(token: string, practiceArea: string): Promise<MatterTemplate[]> {
    const allTemplates = await this.getMatterTemplates(token);
    return allTemplates.filter(template => 
      !template.practiceArea || template.practiceArea === practiceArea
    );
  }

  /**
   * Get a specific template by ID
   * @param token - Keycloak JWT token from React context
   */
  static async getTemplateById(token: string, templateId: string): Promise<MatterTemplate | null> {
    const templates = await this.getMatterTemplates(token);
    return templates.find(template => template.id === templateId) || null;
  }

  /**
   * Create a new matter template
   * @param token - Keycloak JWT token from React context
   */
  static async createMatterTemplate(token: string, template: Omit<MatterTemplate, 'id' | 'createdAt'>): Promise<MatterTemplate> {
    try {
      const response = await fetch('/api/v1/admin/firm-settings/matter-configuration/templates', {
        method: 'POST',
        headers: this.getAuthHeaders(token),
        body: JSON.stringify(template),
      });

      if (response.ok) {
        return await response.json();
      } else {
        throw new Error('Failed to create matter template');
      }
    } catch (error) {
      console.error('Error creating matter template:', error);
      throw error;
    }
  }

  /**
   * Update an existing matter template
   * @param token - Keycloak JWT token from React context
   */
  static async updateMatterTemplate(token: string, templateId: string, updates: Partial<MatterTemplate>): Promise<MatterTemplate> {
    try {
      const response = await fetch(`/api/v1/admin/firm-settings/matter-configuration/templates/${templateId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(token),
        body: JSON.stringify(updates),
      });

      if (response.ok) {
        return await response.json();
      } else {
        throw new Error('Failed to update matter template');
      }
    } catch (error) {
      console.error('Error updating matter template:', error);
      throw error;
    }
  }

  /**
   * Delete a matter template
   * @param token - Keycloak JWT token from React context
   */
  static async deleteMatterTemplate(token: string, templateId: string): Promise<void> {
    try {
      const response = await fetch(`/api/v1/admin/firm-settings/matter-configuration/templates/${templateId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(token),
      });

      if (!response.ok) {
        throw new Error('Failed to delete matter template');
      }
    } catch (error) {
      console.error('Error deleting matter template:', error);
      throw error;
    }
  }
} 